import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaClock, FaCalendarAlt, FaCheckCircle, FaTimes, FaHandshake } from 'react-icons/fa';
import { CustomButton10, CustomButton10Ca, CustomButton10Co, ModernDropdown } from '../../Templates';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useToast } from '../../hooks/ToastContext';
import { supabase } from '../../data/supabaseClient';
import Spinner from '../../common/Spinner';
import { UnifiedDatePicker, UnifiedTimePicker } from '../../common/UnifiedDateTimePicker';

const MeetingAgreement = ({ 
  ride, 
  currentUser, 
  otherUser, 
  userRole, 
  onUpdate, 
  addSystemMessage, 
  createNotification,
  post // Add post prop to get default times
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Initialize with post's travel date and time if available
  const getDefaultDate = () => {
    if (post?.travel_date) {
      return new Date(post.travel_date);
    }
    return new Date();
  };
  
  const getDefaultTime = () => {
    if (post?.travel_time) {
      const [hours, minutes] = post.travel_time.split(':');
      const time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return time;
    }
    return null;
  };
  
  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  const [selectedTime, setSelectedTime] = useState(getDefaultTime());
  const [waitingDuration, setWaitingDuration] = useState(3);
  const [isProposing, setIsProposing] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);

  // Check if there's a pending proposal
  const hasPendingProposal = ride.meeting_time_proposal && !ride.meeting_time;
  const isProposalFromCurrentUser = ride.meeting_time_proposal?.proposed_by === currentUser.id;
  const canPropose = !hasPendingProposal || isProposalFromCurrentUser;

  // Combine date and time for proposal
  const getCombinedDateTime = () => {
    if (selectedDate && selectedTime) {
      const combined = new Date(selectedDate);
      combined.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      return combined.toISOString();
    }
    return null;
  };

  // Handle proposing a meeting time
  const handleProposeTime = async () => {
    const proposedTime = getCombinedDateTime();
    
    if (!proposedTime) {
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: t('ride_actions.select_date_time')
      });
      return;
    }

    setIsProposing(true);
    
    try {
      // Create the meeting time proposal
      const proposal = {
        proposed_by: currentUser.id,
        proposed_time: proposedTime,
        waiting_duration: waitingDuration,
        proposed_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('rides')
        .update({
          meeting_time_proposal: proposal,
          status: 'meeting_agreement',
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Create notification for other user
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      const timeString = new Date(proposedTime).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });

      await createNotification(
        otherUserId,
        'meeting_time_proposed',
        t('ride_actions.meeting_time_proposed', { role: userRole, time: timeString }),
        ride.id
      );

      await addSystemMessage(
        t('ride_actions.meeting_time_proposed_system', { 
          role: userRole, 
          time: timeString, 
          duration: waitingDuration 
        })
      );

      addToast({
        type: 'success',
        title: t('ride_actions.time_proposed'),
        description: t('ride_actions.meeting_time_proposed_desc', { time: timeString })
      });

      setShowTimeModal(false);
      
    } catch (error) {
      console.error('Error proposing meeting time:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: t('ride_actions.failed_propose_time')
      });
    } finally {
      setIsProposing(false);
    }
  };

  // Handle responding to a meeting time proposal
  const handleRespondToProposal = async (accept) => {
    setIsResponding(true);
    
    try {
      let updateData = {
        updated_at: new Date().toISOString()
      };

      if (accept) {
        // Accept the proposal
        updateData.meeting_time = ride.meeting_time_proposal.proposed_time;
        updateData.meeting_duration_minutes = ride.meeting_time_proposal.waiting_duration;
        updateData.meeting_agreed_at = new Date().toISOString();
        updateData.status = 'meeting_agreed';
        // Clear the proposal
        updateData.meeting_time_proposal = null;
      } else {
        // Decline the proposal
        updateData.meeting_time_proposal = null;
        updateData.status = 'confirmed'; // Back to confirmed status
      }

      const { error } = await supabase
        .from('rides')
        .update(updateData)
        .eq('id', ride.id);

      if (error) throw error;

      // Create notification for other user
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      const timeString = new Date(ride.meeting_time_proposal.proposed_time).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });

      await createNotification(
        otherUserId,
        accept ? 'meeting_time_agreed' : 'meeting_time_declined',
        accept ? 
          t('ride_actions.meeting_time_confirmed', { time: timeString }) : 
          t('ride_actions.meeting_time_declined_by', { role: userRole }),
        ride.id
      );

      await addSystemMessage(
        accept ? 
          t('ride_actions.meeting_time_confirmed_system', { time: timeString }) : 
          t('ride_actions.meeting_time_declined_system', { role: userRole })
      );

      addToast({
        type: accept ? 'success' : 'info',
        title: accept ? t('ride_actions.meeting_confirmed') : t('ride_actions.proposal_declined'),
        description: accept ? 
          t('ride_actions.meeting_set_for', { time: timeString }) : 
          t('ride_actions.can_propose_different')
      });
      
    } catch (error) {
      console.error('Error responding to proposal:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: t('ride_actions.failed_respond_proposal')
      });
    } finally {
      setIsResponding(false);
    }
  };

  // Get today's date for min time selection
  const getMinDate = () => {
    return new Date();
  };

  // Time Proposal Modal Component
  const TimeProposalModal = () => {
    const handleConfirm = () => {
      if (selectedDate && selectedTime) {
        handleProposeTime();
      }
    };

    return ReactDOM.createPortal(
      <div 
        className="RAC-modal-overlay-v2" 
        onClick={(e) => {
          if (e.target === e.currentTarget && !isProposing) {
            setShowTimeModal(false);
            // Reset to post defaults if cancelled
            setSelectedDate(getDefaultDate());
            setSelectedTime(getDefaultTime());
          }
        }}
      >
        <div className="RAC-modal2-v2">
          <div className="RAC-time-proposal-dialog">
            <div className="RAC-dialog-content">
              <h4>{t('ride_actions.propose_meeting_time')}</h4>
              
              <div className="RAC-time-selector-grid">
                <div className="RAC-date-section">
                  <label><FaCalendarAlt size={12} /> {t('ride_actions.date')}</label>
                  <UnifiedDatePicker
                    selected={selectedDate}
                    onChange={setSelectedDate}
                    theme="default"
                    placeholder={t('ride_actions.select_date')}
                    minDate={getMinDate()}
                    className="RAC-modal-picker"
                  />
                </div>
                
                <div className="RAC-time-section">
                  <label><FaClock size={12} /> {t('ride_actions.time')}</label>
                  <UnifiedTimePicker
                    selected={selectedTime}
                    onChange={setSelectedTime}
                    theme="default"
                    placeholder={t('ride_actions.select_time')}
                    className="RAC-modal-picker"
                  />
                </div>
                
                <div className="RAC-wait-section">
                  <ModernDropdown
                    label={t('ride_actions.wait_time')}
                    value={`${waitingDuration} ${t('ride_actions.minutes')}`}
                    options={[
                      `3 ${t('ride_actions.minutes')}`,
                      `5 ${t('ride_actions.minutes')}`,
                      `10 ${t('ride_actions.minutes')}`,
                      `15 ${t('ride_actions.minutes')}`
                    ]}
                    onSelect={(option) => {
                      const minutes = parseInt(option.split(' ')[0]);
                      setWaitingDuration(minutes);
                    }}
                    icon={<FaClock size={12} />}
                    className="RAC-modal-dropdown"
                  />
                </div>
              </div>
              
              {selectedDate && selectedTime && (
                <div className="RAC-time-preview">
                  <div className="RAC-preview-title">
                    <FaCheckCircle size={12} /> {t('ride_actions.proposed_time')}
                  </div>
                  <div className="RAC-preview-details">
                    {selectedDate.toLocaleDateString(i18n.language === 'lv' ? 'lv-LV' : i18n.language === 'ru' ? 'ru-RU' : 'en-GB', {
                      weekday: 'long',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                    {' '}{t('ride_actions.at')}{' '}
                    {selectedTime.toLocaleTimeString(i18n.language === 'lv' ? 'lv-LV' : i18n.language === 'ru' ? 'ru-RU' : 'en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                  </div>
                  <div className="RAC-preview-wait">
                    {t('ride_actions.wait_time')}: {waitingDuration} {t('ride_actions.minutes')}
                  </div>
                </div>
              )}
              
              <div className="RAC-dialog-actions">
                <CustomButton10Ca
                  onClick={() => {
                    setShowTimeModal(false);
                    // Reset to post defaults if cancelled
                    setSelectedDate(getDefaultDate());
                    setSelectedTime(getDefaultTime());
                  }}
                  label={t('buttons.cancel')}
                  disabled={isProposing}
                />
                <CustomButton10Co
                  onClick={handleConfirm}
                  disabled={isProposing || !selectedDate || !selectedTime}
                >
                  {isProposing ? (
                    <Spinner size="small" showText={false} />
                  ) : (
                    t('ride_actions.propose')
                  )}
                </CustomButton10Co>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render proposal response
  const renderProposalResponse = () => {
    const proposal = ride.meeting_time_proposal;
    const timeString = new Date(proposal.proposed_time).toLocaleTimeString(i18n.language === 'lv' ? 'lv-LV' : i18n.language === 'ru' ? 'ru-RU' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const dateString = new Date(proposal.proposed_time).toLocaleDateString(i18n.language === 'lv' ? 'lv-LV' : i18n.language === 'ru' ? 'ru-RU' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return (
      <div className="RAC-proposal-response-compact">
        <div className="RAC-proposal-info-compact">
          <div className="RAC-proposal-header-compact">
            <FaHandshake size={12} />
            <span>{t('ride_actions.proposal')}</span>
          </div>
          <div className="RAC-proposal-details-compact">
            <span>{dateString} {t('ride_actions.at')} {timeString}</span>
            <span>({proposal.waiting_duration} {t('ride_actions.minutes')} {t('ride_actions.wait_time').toLowerCase()})</span>
          </div>
        </div>
        
        <div className="RAC-proposal-actions-compact">
          <CustomButton10Ca
            onClick={() => handleRespondToProposal(false)}
            disabled={isResponding}
          >
            {isResponding ? <Spinner size="small" showText={false} /> : t('ride_actions.no')}
          </CustomButton10Ca>
          <CustomButton10Co
            onClick={() => handleRespondToProposal(true)}
            disabled={isResponding}
          >
            {isResponding ? <Spinner size="small" showText={false} /> : t('ride_actions.yes')}
          </CustomButton10Co>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="RAC-meeting-agreement-compact">
      <div className="RAC-meeting-content-compact">
        {hasPendingProposal && !isProposalFromCurrentUser ? (
          // Show proposal from other user
          renderProposalResponse()
        ) : (
          // Show time selector or propose button
          <div className="RAC-propose-section-compact">
            {hasPendingProposal && isProposalFromCurrentUser ? (
              <div className="RAC-waiting-response-compact">
                <div className="RAC-waiting-message-compact">
                  <FaClock size={12} />
                  <span>{t('ride_actions.waiting_for_response')}</span>
                </div>
                <CustomButton10Ca
                  onClick={() => setShowTimeModal(true)}
                  label={t('ride_actions.change')}
                />
              </div>
            ) : (
              <div className="RAC-propose-actions-compact">
                <CustomButton10
                  onClick={() => setShowTimeModal(true)}
                  label={t('ride_actions.set_time')}
                  disabled={!canPropose}
                >
                  <FaCalendarAlt />
                </CustomButton10>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Time Proposal Modal */}
      {showTimeModal && <TimeProposalModal />}
    </div>
  );
};

export default MeetingAgreement;